import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Settings, FileText, Save, RotateCcw, Eye, X, Upload, Check, Trash2, FileEdit, Package, ClipboardList, Users, Gift, BarChart3, Construction, FileSpreadsheet, ArrowRight, AlertTriangle, Sparkles, Mail, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { EventNoticeDialog } from './Documents/EventNoticeDialog';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import DraftProgrammePanel from './Documents/DraftProgrammePanel';
import CompetitionDrawsPanel from './Documents/CompetitionDrawsPanel';
import FinalProgrammePanel from './Documents/FinalProgrammePanel';
import RunSheetPanel from './Documents/RunSheetPanel';
import JudgesSheetsPanel from './Documents/JudgesSheetsPanel';
import EntryPackEmailDialog from './Documents/EntryPackEmailDialog';
import CoveringEmailDialog from './Documents/CoveringEmailDialog';
import LastSentLine from './Documents/LastSentLine';
import ThankYouEmailPanel from './Documents/ThankYouEmailPanel';
import PostEventReportPanel from './Documents/PostEventReportPanel';
import ResultsPanel from './Documents/ResultsPanel';
import EntryFormsPanel from './Documents/EntryFormsPanel';
import SummaryPackPanel from './Documents/SummaryPackPanel';
import OOSPlanningForm from '@/components/order-of-skate/OOSPlanningForm';
import OOSWorksheetsPanel from '@/pages/Documents/OOSWorksheetsPanel';

// MS365 fonts offered in the Typography card + their metric- /
// visually-compatible open-source substitutes used by the
// LibreOffice PDF engine when documents are exported.
//
// These are the actual fonts bundled / present on the server's
// PDF pipeline (see ``backend/server.py::ensure_bundled_fonts``):
//   * Carlito        — metric-compatible with Calibri (bundled)
//   * Caladea        — metric-compatible with Cambria (bundled)
//   * Liberation Sans  — metric-compatible with Arial
//   * Liberation Serif — metric-compatible with Times New Roman
//   * Bitstream Charter — visual cousin of Georgia (wide, sturdy
//                         serif designed for screen reading). The
//                         closest available match given Georgia's
//                         distinctive proportions; no metric-
//                         compatible substitute exists.
const MS365_FONTS = ['Calibri', 'Arial', 'Times New Roman', 'Georgia'];
const PDF_FONT_MAP = {
  Calibri: { name: 'Carlito', family: 'sans-serif' },
  Arial: { name: 'Liberation Sans', family: 'sans-serif' },
  'Times New Roman': { name: 'Liberation Serif', family: 'serif' },
  Georgia: { name: 'Bitstream Charter', family: 'serif' },
};
const PDF_FONT_FALLBACK = { name: 'Liberation Sans', family: 'sans-serif' };
const pdfFontFor = (msFont) => PDF_FONT_MAP[msFont] || PDF_FONT_FALLBACK;

// Colour scheme presets
const colourPresets = {
  brand: {
    h1: '#3D5A3D',
    h2: '#5C4033',
    h3: '#4A6B4A',
    h4: '#7D5A44',
    tableHeader: '#5A7D5A',
    highlight: '#D4A84B',
    bodyText: '#1A1A1A',
    secondaryText: '#666666'
  },
  grayscale: {
    h1: '#000000',
    h2: '#2D2D2D',
    h3: '#595959',
    h4: '#858585',
    tableHeader: '#1A1A1A',
    highlight: '#B8B8B8',
    bodyText: '#000000',
    secondaryText: '#595959'
  },
  high_contrast: {
    h1: '#2E74B5',
    h2: '#C00000',
    h3: '#196B24',
    h4: '#333333',
    tableHeader: '#444444',
    highlight: '#D4A84B',
    bodyText: '#000000',
    secondaryText: '#333333'
  }
};

// Default document settings
const defaultSettings = {
  paperSize: 'A4',
  orientation: 'portrait',
  marginPreset: 'standard',
  margins: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 },
  headingFont: 'Calibri',
  bodyFont: 'Calibri',
  tableFont: 'Calibri',
  fontSizes: {
    documentTitle: 20,
    heading1: 16,
    heading2: 14,
    heading3: 12,
    heading4: 11,
    bodyText: 11,
    tableHeader: 11,
    tableBody: 10,
    highlight: 11,
    secondaryText: 9,
    headerFooter: 9
  },
  // Per-style MS365 font override — ``null`` means "follow the
  // Default Heading / Body Font" (the heading-family keys h1-h4 link
  // to ``headingFont``, every other key links to ``bodyFont``). Once
  // a row's dropdown is set to a real font name, it stays overridden
  // until the user resets it. Backed by
  // ``utils/document_settings_resolver.py`` on the backend.
  styleFonts: {
    h1: null, h2: null, h3: null, h4: null,
    tableHeader: null, highlight: null, bodyText: null, secondaryText: null,
  },
  lineSpacing: 1.08,
  paragraphSpacing: { before: 0, after: 9, lineSpacing: 1.08, lineSpacingType: 'multiple' },
  colourScheme: 'brand',
  colours: { ...colourPresets.brand },
  includeHeader: true,
  includeFooter: true,
  includeLogo: true,
  includePageNumbers: true,
  pageNumberFormat: 'Page X of Y',
  firstPageDifferent: false,
  tableBorderColour: '#CCCCCC',
  tableHeaderBackground: '#E8F0E8',
  alternatingRows: true,
  alternatingRowColour: '#F9F9F9',
  defaultOutputFormat: 'pdf'
};

// Style row metadata for the Document Styling table. Internal keys
// match the colour palette + ``styleFonts`` keys; the display labels
// match the equivalent Word built-in style names.
const STYLE_ROWS = [
  { key: 'h1', label: 'Heading 1', sizeKey: 'heading1' },
  { key: 'h2', label: 'Heading 2', sizeKey: 'heading2' },
  { key: 'h3', label: 'Heading 3', sizeKey: 'heading3' },
  { key: 'h4', label: 'Heading 4', sizeKey: 'heading4' },
  { key: 'tableHeader', label: 'Table Header', sizeKey: 'tableHeader' },
  { key: 'highlight', label: 'Strong', sizeKey: 'highlight' },
  { key: 'bodyText', label: 'Normal', sizeKey: 'bodyText' },
  { key: 'secondaryText', label: 'Caption', sizeKey: 'secondaryText' },
];
const HEADING_FAMILY_STYLE_KEYS = new Set(['h1', 'h2', 'h3', 'h4']);
const styleSizeDefaults = {
  h1: 16, h2: 14, h3: 12, h4: 11,
  tableHeader: 11, highlight: 11, bodyText: 11, secondaryText: 9,
};

// Resolve the effective MS365 font for a single style row: explicit
// override wins, otherwise it follows the Default Heading / Body font
// (mirrors ``utils/document_settings_resolver.resolve_style_font``).
const resolveStyleFont = (settings, key) => {
  const explicit = settings?.styleFonts?.[key];
  if (explicit) return explicit;
  return HEADING_FAMILY_STYLE_KEYS.has(key)
    ? (settings?.headingFont || 'Calibri')
    : (settings?.bodyFont || 'Calibri');
};

// Convert "#RRGGBB" to "R, G, B" — used by the Styles Detail table
// next to each colour swatch.
const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return '0, 0, 0';
  const m = hex.replace('#', '').match(/^([0-9a-fA-F]{6})$/);
  if (!m) return '0, 0, 0';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  // Allow deep-linking into a specific tab + document tile + competition
  // via query string — e.g. the Order of Skate page's "back" arrow uses
  // /documents?tab=order-programme&doc=order_of_skate&comp=<cid> so the
  // user lands back where they were rather than on a blank Documents
  // page. Read once on mount; falls back to the default tab if absent.
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'competition-entries';
  const initialDoc = searchParams.get('doc') || null;
  const initialComp = searchParams.get('comp') || '';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedDocument, setSelectedDocument] = useState(initialDoc);
  
  // Existing state
  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState(initialComp);
  const [settings, setSettings] = useState({ ...defaultSettings });
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [publishedDocs, setPublishedDocs] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [eventNoticeDialogOpen, setEventNoticeDialogOpen] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  // Feb 2026 Custom Event Notice upload state
  const [eventNoticeCustom, setEventNoticeCustom] = useState(null);
  const [customUploadOpen, setCustomUploadOpen] = useState(false);
  const [customUploadEventFile, setCustomUploadEventFile] = useState(null);
  const [customUploadFigureFile, setCustomUploadFigureFile] = useState(null);
  const [customUploading, setCustomUploading] = useState(false);

  // Competition delete dialog — fired by the Summary Pack panel after
  // a successful archive download so the organiser can clean up
  // straight away (owner spec Feb 2026).
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Entry Confirmation state
  const [clubsWithEntries, setClubsWithEntries] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');
  const [competitionsWithEntries, setCompetitionsWithEntries] = useState([]);
  const [entryEmailDialogOpen, setEntryEmailDialogOpen] = useState(false);
  // Separate club selector for the Email tile so the Generate-tile club
  // (used for per-club downloads) can be set independently of the
  // intended email recipient(s).
  const [entryEmailClub, setEntryEmailClub] = useState('all');

  // Entry Pack state
  const [entryPackComp, setEntryPackComp] = useState('');
  const [entryPackClubs, setEntryPackClubs] = useState([]);
  const [entryPackSelectedIds, setEntryPackSelectedIds] = useState([]);
  const [entryPackDialogOpen, setEntryPackDialogOpen] = useState(false);
  const [loadingEntryPackClubs, setLoadingEntryPackClubs] = useState(false);
  
  // Order of Skate (shared) — used for both Draft and Final Programme panels
  const [competitionsWithOOS, setCompetitionsWithOOS] = useState([]);

  
  // Reports tab state
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Entry Summary Template state
  const [templateCompetitionId, setTemplateCompetitionId] = useState('');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Competition Permit state
  const [permitCompetitionId, setPermitCompetitionId] = useState('');
  const [permitFormData, setPermitFormData] = useState({
    contact_name: '',
    postal_address_street: '',
    postal_address_suburb: '',
    postal_address_city: '',
    phone_number: '',
    email: '',
    website_address: '',
    website_option: 'own', // 'competition_page', 'own', 'none'
    other_details: '',
    position: '',
    signatory_consent: false
  });
  const [generatingPermit, setGeneratingPermit] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Order of Skate Planning state
  const [oosSelectedComp, setOosSelectedComp] = useState('');
  const [oosCompetitionDetails, setOosCompetitionDetails] = useState(null);
  const [oosPlanningSettings, setOosPlanningSettings] = useState(null);
  const [showOOSPlanningForm, setShowOOSPlanningForm] = useState(false);
  const [oosScheduleExists, setOosScheduleExists] = useState(false);
  const [showResetOOSDialog, setShowResetOOSDialog] = useState(false);

  // Fetch current user for permit defaults
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setCurrentUser(res.data))
      .catch(() => {});
  }, []);

  // Fetch permit form defaults when competition is selected
  const fetchPermitDefaults = async (compId) => {
    if (!compId) return;
    try {
      const res = await axios.get(`${API}/permits/form-defaults/${compId}`, { withCredentials: true });
      setPermitFormData(prev => ({
        ...prev,
        contact_name: res.data.contact_name || prev.contact_name,
        email: res.data.email || prev.email
      }));
    } catch (err) {
      console.log('Could not fetch permit defaults');
    }
  };

  // Fetch functions
  const fetchClubsWithEntries = async (compId) => {
    if (!compId) {
      setClubsWithEntries([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/documents/clubs-with-entries/${compId}`, { withCredentials: true });
      const clubs = res.data.clubs || [];
      setClubsWithEntries(clubs);
      if (clubs.length === 1) {
        setSelectedClub(clubs[0].club_id);
      } else {
        setSelectedClub('all');
      }
    } catch (err) {
      console.log('Could not fetch clubs with entries');
      setClubsWithEntries([]);
    }
  };
  
  // Fetch competitions that have entries (for Entry Confirmations dropdown)
  const fetchCompetitionsWithEntries = async () => {
    try {
      const res = await axios.get(`${API}/entries/competition-counts`, { withCredentials: true });
      // Filter to only competitions with entry_count > 0
      const compsWithEntries = (res.data.competitions || []).filter(c => c.entry_count > 0);
      setCompetitionsWithEntries(compsWithEntries);
    } catch (err) {
      console.log('Could not fetch competitions with entries');
      setCompetitionsWithEntries([]);
    }
  };
  

  
  const fetchCompetitionsWithOOS = async () => {
    try {
      const res = await axios.get(`${API}/order-of-skate/competitions-with-oos`, { withCredentials: true });
      setCompetitionsWithOOS(res.data.competitions || []);
    } catch { setCompetitionsWithOOS([]); }
  };

  // ----- Sync the global "Select Competition" choice into every tile that
  //       still owns its own competition-id state, plus fire any related
  //       data fetches those tiles previously did inline. This keeps each
  //       tile's complex internals untouched while collapsing the visible
  //       selectors down to a single global one.
  useEffect(() => {
    setOosSelectedComp(selectedComp || '');
    setEntryPackComp(selectedComp || '');
    setTemplateCompetitionId(selectedComp || '');
    setPermitCompetitionId(selectedComp || '');
    if (selectedComp) {
      fetchClubsWithEntries(selectedComp);
      fetchOOSCompetitionData(selectedComp);
      fetchPermitDefaults(selectedComp);
      // Entry Pack tile depends on its own clubs list.
      (async () => {
        try {
          const res = await axios.get(
            `${API}/entry-pack-email/${selectedComp}/eligible-clubs`,
            { withCredentials: true }
          );
          setEntryPackClubs(res.data.clubs || []);
          setEntryPackSelectedIds([]);
        } catch {
          setEntryPackClubs([]);
          setEntryPackSelectedIds([]);
        }
      })();
    } else {
      setClubsWithEntries([]);
      setOosCompetitionDetails(null);
      setOosPlanningSettings(null);
      setShowOOSPlanningForm(false);
      setEntryPackClubs([]);
      setEntryPackSelectedIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComp]);


  const fetchPublishedDocs = async (compId) => {
    if (!compId) {
      setPublishedDocs([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/published-documents/${compId}`, { withCredentials: true });
      setPublishedDocs(res.data || []);
    } catch (err) {
      console.log('No published documents');
      setPublishedDocs([]);
    }
  };

  // Alias used after mutating published docs via the Custom Event
  // Notice flow — keeps the Event Notice panel in sync.
  const loadPublishedDocuments = fetchPublishedDocs;

  const refreshCustomEventNotice = async (compId) => {
    if (!compId) {
      setEventNoticeCustom(null);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/documents/custom-event-notice/${compId}`,
        { withCredentials: true },
      );
      setEventNoticeCustom(res.data || null);
    } catch (err) {
      setEventNoticeCustom(null);
    }
  };

  const submitCustomEventNoticeUpload = async () => {
    if (!selectedComp || !customUploadEventFile) {
      toast.error('Please choose an Event Notice PDF first');
      return;
    }
    if (!customUploadEventFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Event Notice must be a PDF');
      return;
    }
    if (customUploadFigureFile && !customUploadFigureFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Figure Draw must be a PDF');
      return;
    }
    const MAX = 10 * 1024 * 1024;
    if (customUploadEventFile.size > MAX || (customUploadFigureFile && customUploadFigureFile.size > MAX)) {
      toast.error('Each file must be 10 MB or smaller');
      return;
    }
    setCustomUploading(true);
    try {
      const form = new FormData();
      form.append('event_notice_file', customUploadEventFile);
      if (customUploadFigureFile) form.append('figure_draw_file', customUploadFigureFile);
      await axios.post(
        `${API}/documents/custom-event-notice/${selectedComp}`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Uploaded and published');
      setCustomUploadOpen(false);
      setCustomUploadEventFile(null);
      setCustomUploadFigureFile(null);
      await refreshCustomEventNotice(selectedComp);
      await fetchPublishedDocs(selectedComp);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCustomUploading(false);
    }
  };
  
  // Fetch OOS competition details and planning settings
  const fetchOOSCompetitionData = async (compId) => {
    if (!compId) {
      setOosCompetitionDetails(null);
      setOosPlanningSettings(null);
      setShowOOSPlanningForm(false);
      setOosScheduleExists(false);
      return;
    }
    try {
      // Fetch competition details, planning settings, and OOS schedule in parallel
      const [compRes, settingsRes, oosRes] = await Promise.all([
        axios.get(`${API}/competitions/${compId}`, { withCredentials: true }),
        axios.get(`${API}/order-of-skate/${compId}/planning-settings`, { withCredentials: true }),
        axios.get(`${API}/order-of-skate/${compId}`, { withCredentials: true })
      ]);
      
      setOosCompetitionDetails(compRes.data);
      setOosPlanningSettings(settingsRes.data.exists ? settingsRes.data : null);
      setOosScheduleExists(oosRes.data.exists || false);
      setShowOOSPlanningForm(true);
    } catch (err) {
      console.error('Failed to fetch OOS competition data:', err);
      setOosCompetitionDetails(null);
      setOosPlanningSettings(null);
      setShowOOSPlanningForm(false);
      setOosScheduleExists(false);
    }
  };
  
  useEffect(() => {
    axios.get(`${API}/competitions`, { withCredentials: true })
      .then(res => {
        setCompetitions(res.data);
        // Auto-select the only competition (single-comp environments)
        // BUT respect a URL-seeded comp — the back-arrow deep-link
        // must not be overridden by this default-pick path.
        if (res.data.length === 1 && !initialComp) {
          setSelectedComp(res.data[0].competition_id);
          fetchPublishedDocs(res.data[0].competition_id);
        }
        // Fetch competitions with entries after we know auth is working
        fetchCompetitionsWithEntries();
        fetchCompetitionsWithOOS();
      })
      .catch(err => console.error(err));
    
    axios.get(`${API}/document-settings`, { withCredentials: true })
      .then(res => {
        if (res.data && Object.keys(res.data).length > 0) {
          setSettings({ ...defaultSettings, ...res.data });
        }
      })
      .catch(err => console.log('Using default settings'));
    
    axios.get(`${API}/content/schedules`, { withCredentials: true })
      .then(res => setSchedules(res.data || []))
      .catch(err => console.log('Failed to load schedules'));
  }, []);
  
  useEffect(() => {
    if (selectedComp) {
      fetchPublishedDocs(selectedComp);
      fetchClubsWithEntries(selectedComp);
      refreshCustomEventNotice(selectedComp);
    } else {
      setEventNoticeCustom(null);
    }
  }, [selectedComp]);

  // Keep the Order of Skate panel in sync with the top-level competition
  // picker. The panel manages its own ``oosSelectedComp`` state for
  // historical reasons; mirroring ``selectedComp`` into it ensures the
  // form populates correctly whether the user clicks the tile after
  // picking a comp, or picks/changes the comp after opening the tile.
  useEffect(() => {
    if (selectedDocument !== 'order_of_skate') return;
    if (!selectedComp) return;
    if (oosSelectedComp === selectedComp) return;
    setOosSelectedComp(selectedComp);
    void fetchOOSCompetitionData(selectedComp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComp, selectedDocument]);

  // Document generation and publishing handlers
  const isDocumentPublished = (docType) => publishedDocs.some(d => d.document_type === docType);
  const getPublishedDoc = (docType) => publishedDocs.find(d => d.document_type === docType);

  const handlePublishDocument = async (docType) => {
    if (!selectedComp) {
      toast.error('Please select a competition');
      return;
    }
    setPublishing(true);
    try {
      await axios.post(
        `${API}/documents/publish`,
        { competition_id: selectedComp, document_type: docType },
        { withCredentials: true }
      );
      
      toast.success(`${docType === 'event_notice' ? 'Event Notice' : 'Document'} published successfully`);
      fetchPublishedDocs(selectedComp);
    } catch (error) {
      console.error(error);
      // FastAPI 422 responses send ``detail`` as an array of validation
      // error objects; coerce to a string before toasting so React
      // never tries to render an object as a child.
      const detail = error.response?.data?.detail;
      let msg = 'Failed to publish document';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail
          .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
          .join('; ');
      } else if (detail && typeof detail === 'object') {
        msg = detail?.msg || JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishDocument = async (documentId) => {
    if (!documentId) return;
    try {
      await axios.delete(`${API}/published-documents/${documentId}`, { withCredentials: true });
      toast.success('Document unpublished');
      fetchPublishedDocs(selectedComp);
    } catch (error) {
      toast.error('Failed to unpublish document');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(`${API}/document-settings`, settings, { withCredentials: true });
      toast.success('Settings saved successfully');
      setSettingsChanged(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleResetSettings = () => {
    setSettings({ ...defaultSettings });
    setSettingsChanged(true);
    toast.info('Settings reset to defaults');
  };

  const handlePreviewSettings = async () => {
    setPreviewLoading(true);
    try {
      const response = await axios.post(`${API}/documents/preview`, settings, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleColourSchemeChange = (scheme) => {
    if (scheme === 'custom') {
      setSettings(prev => ({ ...prev, colourScheme: scheme }));
    } else if (colourPresets[scheme]) {
      const newColours = { ...colourPresets[scheme] };
      setSettings(prev => ({ ...prev, colourScheme: scheme, colours: newColours }));
    }
    setSettingsChanged(true);
  };

  // Tab configuration
  const tabs = [
    { id: 'competition-entries', label: 'Competition & Entries', icon: FileText },
    { id: 'order-programme', label: 'Order of Events & Programme', icon: ClipboardList },
    { id: 'officials', label: "Officials' Documents", icon: Users },
    { id: 'wrap-up', label: 'Wrap Up Documents', icon: Gift },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'settings', label: 'Default Document Settings', icon: Settings },
  ];

  // Document tiles configuration
  const competitionEntriesDocs = [
    { id: 'competition_permit', title: 'Competition Permit', description: 'Official permit document for the competition.', status: 'active' },
    { id: 'event_notice', title: 'Event Notice', description: 'Official covering letter for the event with competition details, venue info, and rules.', status: 'active' },
    { id: 'entry_summary', title: 'Entry Summary Template', description: 'Excel template to collect entries with separate worksheets for Individual, Pairs, and Team entries.', status: 'active' },
    { id: 'entry_forms', title: 'Entry Forms (Areas & above)', description: 'Generate Individual & Pairs and Teams/Groups PDF entry forms for clubs.', status: 'active' },
    { id: 'entry_pack', title: 'Entry Pack', description: 'Combined package: Event Notice, Entry Summary, and Entry Forms for distribution.', status: 'active' },
    { id: 'entry_confirmations', title: 'Entry Confirmations', description: 'Confirmation letters sent to clubs listing all entries received.', status: 'active' },
  ];

  const orderProgrammeDocs = [
    { id: 'order_of_skate', title: 'Order of Skate', description: 'Generate and arrange the order of events for the competition.', status: 'active' },
    { id: 'draft_programme', title: 'Draft Programme', description: 'Generate the draft competition programme — can be created before the draws are complete.', status: 'active' },
    { id: 'competition_draws', title: 'Competition Draws', description: 'Generate or enter the draw information for this event.', status: 'active' },
    { id: 'final_programme', title: 'Final Programme', description: 'Generate the final competition programme in versions optimised for screen or print.', status: 'active' },
  ];

  const officialsDocs = [
    { id: 'run_sheet', title: 'Run Sheet', description: 'Detailed timing and order sheet for officials.', status: 'active' },
    { id: 'judges_sheets_figures', title: 'Judges Sheets (White System)', description: 'Per-element judges sheets for non-RollArt events.', status: 'active' },
  ];

  const wrapUpDocs = [
    { id: 'results_summary', title: 'Summary of Results', description: 'Enter placings per grade — generates an Excel workbook of competitive + special-event results.', status: 'active' },
    { id: 'thank_you_email', title: 'Thank You Note to Clubs', description: 'Email template for thanking participating clubs.', status: 'active' },
    { id: 'summary_pack', title: 'Competition Summary Pack', description: 'Download entry information, competition summaries, copies of the programmes for record before deleting the competition.', status: 'active' },
  ];

  const reportsDocs = [
    { id: 'schedule_report', title: 'Schedule Report', description: 'Comprehensive report of all grades within a schedule, organized by discipline.', status: 'active' },
    { id: 'post_event_report', title: 'Post Event Report', description: 'Key insights from the selected competition', status: 'active' },
  ];

  // Render document tile
  const renderDocumentTile = (doc) => {
    const isSelected = selectedDocument === doc.id;
    const isPublished = isDocumentPublished(doc.id);
    
    const handleTileClick = () => {
      // For the Order of Skate tile, render the in-panel configuration
      // form (same pattern as every other tile). If a competition is
      // already selected at the top of the page, sync the OOS panel
      // state to it and load the planning data — that way the form
      // appears populated immediately. The form's "Open Order of
      // Skate" button still provides the jump to the standalone page.
      if (doc.id === 'order_of_skate') {
        setSelectedDocument(doc.id);
        if (selectedComp) {
          setOosSelectedComp(selectedComp);
          void fetchOOSCompetitionData(selectedComp);
        }
        return;
      }
      setSelectedDocument(doc.id);
    };
    
    return (
      <div
        key={doc.id}
        onClick={handleTileClick}
        className={`p-3 border rounded-lg cursor-pointer transition-all ${
          isSelected 
            ? 'border-[#2D4A3E] bg-green-50 shadow-sm' 
            : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
        }`}
        data-testid={`doc-tile-${doc.id}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-stone-800 text-sm">{doc.title}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">{doc.description}</p>
          </div>
          {isPublished && (
            <span className="ml-2 px-2 py-0.5 bg-green-600 text-white text-xs font-medium rounded-full flex items-center gap-1 shrink-0">
              <Check className="h-3 w-3" /> Published
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render form panel for selected document
  const renderFormPanel = () => {
    if (!selectedDocument) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>Select a document to configure and generate</p>
          </div>
        </div>
      );
    }

    // Find the document info
    const allDocs = [...competitionEntriesDocs, ...orderProgrammeDocs, ...officialsDocs, ...wrapUpDocs, ...reportsDocs];
    const doc = allDocs.find(d => d.id === selectedDocument);
    
    if (!doc) return null;

    // Placeholder documents show "Under Construction"
    if (doc.status === 'placeholder') {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Construction className="h-16 w-16 mx-auto mb-4 text-amber-500" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">{doc.title}</h3>
            <p className="text-muted-foreground">This document is under construction.</p>
            <p className="text-sm text-muted-foreground mt-1">Check back soon for updates!</p>
          </div>
        </div>
      );
    }

    // Thank You Note to Clubs — wrap-up email
    if (selectedDocument === 'thank_you_email') {
      return <ThankYouEmailPanel competitionId={selectedComp} />;
    }

    // Post Event Report — reports tab
    if (selectedDocument === 'post_event_report') {
      return <PostEventReportPanel competitionId={selectedComp} />;
    }

    // Summary of Results — wrap-up tab
    if (selectedDocument === 'results_summary') {
      return <ResultsPanel competitionId={selectedComp} />;
    }

    // Competition Summary Pack — wrap-up tab (gated to status=Completed)
    if (selectedDocument === 'summary_pack') {
      const comp = competitions.find((c) => c.competition_id === selectedComp);
      return (
        <SummaryPackPanel
          competitionId={selectedComp}
          competition={comp}
          onArchiveCreated={() => {
            setDeleteTargetId(selectedComp);
            setDeleteDialogOpen(true);
          }}
        />
      );
    }

    // Entry Forms (Areas & above) — Competition & Entries tab
    if (selectedDocument === 'entry_forms') {
      return <EntryFormsPanel competitionId={selectedComp} />;
    }

    // Order of Skate - competition selector and planning form
    if (selectedDocument === 'order_of_skate') {
      return (
        // Outer right-column container at the bottom of this component
        // already provides ``overflow-y-auto`` for the form panel — we
        // intentionally do NOT add a second scroll here (would produce
        // a nested scrollbar). Pure ``space-y-6`` wrapper is enough.
        <div className="space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-stone-800 mb-2">Order of Skate</h3>
              <p className="text-sm text-muted-foreground">
                Generate and arrange the order of events for your competition. Configure initial settings, 
                then generate the schedule.
              </p>
            </div>
            
            {/* Reset button — shown to the competition owner or a super
                user when an OOS schedule exists. Backend enforces the
                same rule via ``require_competition_access``. */}
            {oosSelectedComp && oosScheduleExists && (
              currentUser?.role === 'super' ||
              oosCompetitionDetails?.created_by === currentUser?.user_id
            ) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResetOOSDialog(true)}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
                data-testid="reset-oos-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset Order of Skate
              </Button>
            )}
          </div>

          <div className="hidden">
            <Label>Select Competition *</Label>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={oosSelectedComp}
              onChange={(e) => {
                setOosSelectedComp(e.target.value);
                if (e.target.value) {
                  fetchOOSCompetitionData(e.target.value);
                } else {
                  setOosCompetitionDetails(null);
                  setOosPlanningSettings(null);
                  setShowOOSPlanningForm(false);
                }
              }}
              data-testid="oos-competition-select"
            >
              <option value="">Select a competition</option>
              {competitionsWithEntries.map(c => (
                <option key={c.competition_id} value={c.competition_id}>
                  {c.name} ({c.entry_count} entries)
                </option>
              ))}
            </select>
            {competitionsWithEntries.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">
                No competitions with entries found. Please add entries to a competition first.
              </p>
            )}
          </div>

          {/* Show planning form when competition is selected */}
          {oosSelectedComp && showOOSPlanningForm && oosCompetitionDetails && (
            <Card
              className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
              data-testid="oos-planning-tile"
            >
              <OOSPlanningForm
                competitionId={oosSelectedComp}
                competitionName={oosCompetitionDetails.name}
                competitionType={oosCompetitionDetails.competition_type}
                competitionStartDate={oosCompetitionDetails.start_date}
                competitionEndDate={oosCompetitionDetails.end_date}
                existingSettings={oosPlanningSettings}
                isLocked={oosScheduleExists}
                isSuperUser={currentUser?.role === 'super'}
                onSave={() => {
                  // Refresh planning settings after save
                  fetchOOSCompetitionData(oosSelectedComp);
                }}
                onNavigateToOOS={() => {
                  navigate(`/competitions/${oosSelectedComp}/order-of-skate`);
                }}
              />
            </Card>
          )}

          {/* Show "Skip to OOS" link when no planning needed */}
          {oosSelectedComp && !showOOSPlanningForm && (
            <Card
              className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
              data-testid="oos-open-tile"
            >
              <div>
                <h4 className="text-base font-semibold text-stone-800">Order of Skate Planning</h4>
                <p className="text-sm text-stone-600 mt-1">
                  Planning is complete — open the Order of Skate to arrange events, adjust timing, and finalise the schedule.
                </p>
              </div>
              <Button
                onClick={() => {
                  navigate(`/competitions/${oosSelectedComp}/order-of-skate`);
                }}
                className="btn-primary w-[240px]"
                data-testid="open-order-of-skate-btn"
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Open Order of Skate
              </Button>
            </Card>
          )}

          {/* OOS Worksheet download tiles + email tile. Owner chose
              option (b) — visible whenever a competition is selected,
              even before an OOS is generated. The download buttons
              surface a friendly toast if the worksheet endpoint 404s
              because no OOS exists yet. */}
          {oosSelectedComp && (
            <OOSWorksheetsPanel competitionId={oosSelectedComp} />
          )}

          {!oosSelectedComp && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-stone-700 mb-2">What you can do:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Configure event placement rules (Local/Regional, Figures, Showcases)</li>
                <li>• Generate initial event order from selected grades</li>
                <li>• Drag and drop to reorder events</li>
                <li>• Download timing worksheet for review</li>
                <li>• Detect skater clashes between events</li>
              </ul>
            </div>
          )}
        </div>
      );
    }

    // Competition Permit form
    if (selectedDocument === 'competition_permit') {
      return (
        // No inner overflow-y-auto — see Order of Skate panel note. The
        // right-column form-panel container already scrolls.
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Generate Competition Permit</h3>
            <p className="text-sm text-muted-foreground">
              Generate the official permit document to submit to SkateNZ.
            </p>
          </div>

          <div className="hidden">
            <Label>Competition *</Label>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={permitCompetitionId}
              onChange={(e) => {
                setPermitCompetitionId(e.target.value);
                if (e.target.value) {
                  fetchPermitDefaults(e.target.value);
                }
              }}
              data-testid="permit-competition-select"
            >
              <option value="">Select Competition</option>
              {competitions.map(c => (
                <option key={c.competition_id} value={c.competition_id}>{c.name}</option>
              ))}
            </select>
          </div>

          {permitCompetitionId && (
            <>
              <div className="border-t pt-4">
                <h4 className="font-medium text-stone-700 mb-3">Contact Details</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Contact Name *</Label>
                    <Input
                      value={permitFormData.contact_name}
                      onChange={(e) => setPermitFormData({ ...permitFormData, contact_name: e.target.value })}
                      placeholder="Full name of principal contact"
                      className="mt-1"
                      data-testid="permit-contact-name"
                    />
                  </div>
                  
                  <div>
                    <Label>Postal Address (Street) *</Label>
                    <Input
                      value={permitFormData.postal_address_street}
                      onChange={(e) => setPermitFormData({ ...permitFormData, postal_address_street: e.target.value })}
                      placeholder="Street address"
                      className="mt-1"
                      data-testid="permit-street"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Suburb</Label>
                      <Input
                        value={permitFormData.postal_address_suburb}
                        onChange={(e) => setPermitFormData({ ...permitFormData, postal_address_suburb: e.target.value })}
                        placeholder="Suburb"
                        className="mt-1"
                        data-testid="permit-suburb"
                      />
                    </div>
                    <div>
                      <Label>Town/City</Label>
                      <Input
                        value={permitFormData.postal_address_city}
                        onChange={(e) => setPermitFormData({ ...permitFormData, postal_address_city: e.target.value })}
                        placeholder="Town or City"
                        className="mt-1"
                        data-testid="permit-city"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Either Suburb or Town/City must be provided</p>
                  
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      value={permitFormData.phone_number}
                      onChange={(e) => setPermitFormData({ ...permitFormData, phone_number: e.target.value })}
                      placeholder="Contact phone number"
                      className="mt-1"
                      data-testid="permit-phone"
                    />
                  </div>
                  
                  <div>
                    <Label>Best contact email for this competition *</Label>
                    <Input
                      type="email"
                      value={permitFormData.email}
                      onChange={(e) => setPermitFormData({ ...permitFormData, email: e.target.value })}
                      placeholder="Contact email address"
                      className="mt-1"
                      data-testid="permit-email"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-stone-700 mb-3">Competition Details</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Website Address</Label>
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="website-own"
                          name="website-option"
                          checked={permitFormData.website_option === 'own'}
                          onChange={() => setPermitFormData({ 
                            ...permitFormData, 
                            website_option: 'own'
                          })}
                          className="h-4 w-4"
                          data-testid="permit-website-own"
                        />
                        <label htmlFor="website-own" className="text-sm">Enter my own web address</label>
                      </div>
                      {permitFormData.website_option === 'own' && (
                        <Input
                          value={permitFormData.website_address}
                          onChange={(e) => setPermitFormData({ ...permitFormData, website_address: e.target.value })}
                          placeholder="https://..."
                          className="ml-6"
                          data-testid="permit-website"
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="website-competition"
                          name="website-option"
                          checked={permitFormData.website_option === 'competition_page'}
                          onChange={() => setPermitFormData({ 
                            ...permitFormData, 
                            website_option: 'competition_page',
                            website_address: ''
                          })}
                          className="h-4 w-4"
                          data-testid="permit-website-competition"
                        />
                        <label htmlFor="website-competition" className="text-sm">Use Competition Page web address</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="website-none"
                          name="website-option"
                          checked={permitFormData.website_option === 'none'}
                          onChange={() => setPermitFormData({ 
                            ...permitFormData, 
                            website_option: 'none',
                            website_address: ''
                          })}
                          className="h-4 w-4"
                          data-testid="permit-website-none"
                        />
                        <label htmlFor="website-none" className="text-sm">No website address required</label>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Other Details</Label>
                    <Input
                      value={permitFormData.other_details}
                      onChange={(e) => setPermitFormData({ ...permitFormData, other_details: e.target.value.slice(0, 80) })}
                      placeholder="Any additional notes (max 80 chars)"
                      maxLength={80}
                      className="mt-1"
                      data-testid="permit-other-details"
                    />
                    <p className="text-xs text-muted-foreground text-right">{permitFormData.other_details.length}/80</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-stone-700 mb-2">Signatory Declaration</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  To submit this permit to SkateNZ, we need a signatory. If you consent to being this signatory, 
                  please provide your name and your role within the organisation which owns the competition.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="signatory-consent"
                      checked={permitFormData.signatory_consent}
                      onChange={(e) => setPermitFormData({ 
                        ...permitFormData, 
                        signatory_consent: e.target.checked
                      })}
                      className="h-4 w-4 mt-0.5"
                      data-testid="permit-signatory-consent"
                    />
                    <label htmlFor="signatory-consent" className="text-sm">
                      I consent to being signatory for this competition permit
                    </label>
                  </div>
                  
                  <div>
                    <Label>Signatory Name</Label>
                    <Input
                      value={permitFormData.contact_name}
                      disabled
                      className="mt-1 bg-stone-50"
                    />
                    <p className="text-xs text-muted-foreground">Uses Contact Name above</p>
                  </div>
                  
                  <div>
                    <Label>Position/Role *</Label>
                    <Input
                      value={permitFormData.position}
                      onChange={(e) => setPermitFormData({ ...permitFormData, position: e.target.value })}
                      placeholder="e.g. Club Secretary, Competition Organiser"
                      className="mt-1"
                      data-testid="permit-position"
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={async () => {
                  // Validate required fields
                  if (!permitFormData.contact_name.trim()) {
                    toast.error('Contact Name is required');
                    return;
                  }
                  if (!permitFormData.postal_address_street.trim()) {
                    toast.error('Postal Address (Street) is required');
                    return;
                  }
                  if (!permitFormData.postal_address_suburb.trim() && !permitFormData.postal_address_city.trim()) {
                    toast.error('Either Suburb or Town/City must be provided');
                    return;
                  }
                  if (!permitFormData.phone_number.trim()) {
                    toast.error('Phone Number is required');
                    return;
                  }
                  if (!permitFormData.email.trim()) {
                    toast.error('Email is required');
                    return;
                  }
                  if (!permitFormData.signatory_consent) {
                    toast.error('You must consent to being signatory for this permit');
                    return;
                  }
                  if (!permitFormData.position.trim()) {
                    toast.error('Position/Role is required');
                    return;
                  }

                  setGeneratingPermit(true);
                  try {
                    // Transform website_option to use_competition_page for backend
                    const payload = {
                      competition_id: permitCompetitionId,
                      contact_name: permitFormData.contact_name,
                      postal_address_street: permitFormData.postal_address_street,
                      postal_address_suburb: permitFormData.postal_address_suburb,
                      postal_address_city: permitFormData.postal_address_city,
                      phone_number: permitFormData.phone_number,
                      email: permitFormData.email,
                      website_address: permitFormData.website_option === 'own' ? permitFormData.website_address : '',
                      use_competition_page: permitFormData.website_option === 'competition_page',
                      other_details: permitFormData.other_details,
                      position: permitFormData.position
                    };
                    
                    const response = await axios.post(
                      `${API}/permits/generate`,
                      payload,
                      { 
                        withCredentials: true,
                        responseType: 'blob'
                      }
                    );
                    
                    // Get filename from content-disposition header
                    const contentDisposition = response.headers['content-disposition'];
                    let filename = 'Competition_Permit.pdf';
                    if (contentDisposition) {
                      const match = contentDisposition.match(/filename="?([^"]+)"?/);
                      if (match) filename = match[1];
                    }
                    
                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('Competition Permit generated successfully');
                  } catch (error) {
                    console.error('Error generating permit:', error);
                    const errorMsg = error.response?.data?.detail || 'Failed to generate permit';
                    toast.error(errorMsg);
                  } finally {
                    setGeneratingPermit(false);
                  }
                }}
                className="btn-primary w-full"
                disabled={generatingPermit}
                data-testid="generate-permit-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                {generatingPermit ? 'Generating...' : 'Generate Permit PDF'}
              </Button>
            </>
          )}
        </div>
      );
    }

    // Event Notice form
    if (selectedDocument === 'event_notice') {
      const custom = eventNoticeCustom || {};
      const hasCustom = !!custom.has_event_notice;
      const openCustomInNewTab = (slot) => {
        const url = `${API}/documents/custom-event-notice/${selectedComp}/view/${slot}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      };
      const confirmDeleteCustom = async () => {
        if (!window.confirm('Delete the Custom Event Notice? The Competition Page\'s Event Notice panel will be empty until you publish a generated one.')) return;
        try {
          await axios.delete(`${API}/documents/custom-event-notice/${selectedComp}`, { withCredentials: true });
          toast.success('Custom Event Notice deleted');
          await refreshCustomEventNotice(selectedComp);
          await loadPublishedDocuments(selectedComp);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to delete');
        }
      };
      return (
        <div className="space-y-6">
          {!hasCustom && (
            <>
              <div>
                <h3 className="text-lg font-semibold text-stone-800 mb-2">Generate Event Notice</h3>
                <p className="text-sm text-muted-foreground">
                  Configure and generate the official event notice document.
                </p>
              </div>

              <div className="hidden">
                <Label>Competition</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={selectedComp}
                  onChange={(e) => setSelectedComp(e.target.value)}
                  data-testid="doc-competition-select"
                >
                  <option value="">Select Competition</option>
                  {competitions.map(c => (
                    <option key={c.competition_id} value={c.competition_id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {selectedComp && (
                <Button
                  onClick={() => {
                    const comp = competitions.find(c => c.competition_id === selectedComp);
                    setSelectedCompetition(comp);
                    setEventNoticeDialogOpen(true);
                  }}
                  className="btn-primary w-full"
                  data-testid="configure-event-notice"
                >
                  <FileEdit className="mr-2 h-4 w-4" />
                  Configure & Generate
                </Button>
              )}
            </>
          )}

          {selectedComp && !hasCustom && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-stone-800 mb-2">Publish to Competition Page</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Make a PDF copy of the Schedule and Figure Draw available via the Competition Page.
              </p>
              {isDocumentPublished('event_notice') ? (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handlePublishDocument('event_notice')}
                    className="btn-primary flex-1"
                    disabled={publishing}
                    data-testid="republish-event-notice"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Re-publish
                  </Button>
                  <Button
                    onClick={() => handleUnpublishDocument(getPublishedDoc('event_notice')?.document_id)}
                    variant="destructive"
                    data-testid="unpublish-event-notice"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => handlePublishDocument('event_notice')}
                  className="btn-primary w-full"
                  disabled={publishing}
                  data-testid="publish-event-notice"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Publish as PDF
                </Button>
              )}
            </div>
          )}

          {selectedComp && !hasCustom && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-stone-800 mb-2">Upload and Publish Custom Event Notices</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Upload a PDF copy of your Event Notice, and separate Figure Draw if you have one, to show on the Competition Page.
              </p>
              <Button
                onClick={() => setCustomUploadOpen(true)}
                className="btn-primary w-full"
                data-testid="upload-custom-event-notice"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload and Publish Event Information to Competition Page
              </Button>
            </div>
          )}

          {selectedComp && hasCustom && (
            <>
              <div>
                <h3 className="text-lg font-semibold text-stone-800 mb-2">Custom Event Notice Published</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  The uploaded PDF is live on the Competition Page and will be included in the Entry Pack. System-generated Event Notice options are disabled while a custom notice is in place.
                </p>
                <div className="rounded-lg border bg-stone-50 px-3 py-2 text-sm space-y-1">
                  <div>
                    <strong>Event Notice:</strong> {custom.event_notice?.filename || 'uploaded.pdf'}
                    {custom.event_notice?.size ? ` · ${(custom.event_notice.size / 1024).toFixed(0)} KB` : ''}
                  </div>
                  {custom.has_figure_draw && (
                    <div>
                      <strong>Figure Draw:</strong> {custom.figure_draw?.filename || 'figure_draw.pdf'}
                      {custom.figure_draw?.size ? ` · ${(custom.figure_draw.size / 1024).toFixed(0)} KB` : ''}
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => openCustomInNewTab('event_notice')}
                  variant="outline"
                  className="w-full mt-3"
                  data-testid="view-custom-event-notice"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Display the Event Notice in a separate window
                </Button>
                {custom.has_figure_draw && (
                  <Button
                    onClick={() => openCustomInNewTab('figure_draw')}
                    variant="outline"
                    className="w-full mt-2"
                    data-testid="view-custom-figure-draw"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Display the Figure Draw in a separate window
                  </Button>
                )}
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-stone-800 mb-2">Delete Custom Event Notice</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Click the button below to replace the custom event notice with a system generated event notice on the Competition Page. This will restore all other options for system generated notices.
                </p>
                <Button
                  onClick={confirmDeleteCustom}
                  variant="destructive"
                  className="w-full"
                  data-testid="delete-custom-event-notice"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Custom Event Notice
                </Button>
              </div>
            </>
          )}
        </div>
      );
    }

    // Entry Confirmations — two-tile layout mirroring the Programme panels
    if (selectedDocument === 'entry_confirmations') {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Entry Confirmations</h3>
            <p className="text-sm text-muted-foreground">
              Confirmation letters listing every skater and event entered by each club.
            </p>
          </div>

          {!selectedComp && (
            <div className="rounded-lg border bg-stone-50 px-3 py-3 text-sm text-stone-600">
              Pick a competition above to continue.
            </div>
          )}

          {selectedComp && clubsWithEntries.length === 0 && (
            <div className="rounded-lg border bg-amber-50 px-3 py-3 text-sm text-amber-900">
              No clubs have entered skaters yet for this competition.
            </div>
          )}

          {selectedComp && clubsWithEntries.length > 0 && (
            <>
              {/* TILE 1: Generate Entry Confirmation (download) */}
              <Card className="p-5 space-y-4 border-stone-200 bg-stone-50/50" data-testid="entry-conf-generate-tile">
                <div>
                  <h4 className="text-base font-semibold text-stone-800">Create Entry Confirmation for Download</h4>
                  <p className="text-sm text-stone-600 mt-1">
                    Download a confirmation letter for a single club, or a ZIP of every club's letter.
                  </p>
                </div>

                <div>
                  <Label htmlFor="ec-download-club" className="text-sm font-medium text-stone-700">
                    Clubs (Select individual clubs or All Clubs Option)
                  </Label>
                  <select
                    id="ec-download-club"
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm"
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                    data-testid="doc-club-select"
                  >
                    <option value="all">All Clubs ({clubsWithEntries.length} clubs)</option>
                    {clubsWithEntries.map(club => (
                      <option key={club.club_id} value={club.club_id}>
                        {club.club_name} ({club.entry_count} entries)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
                  <Button
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        params.append('competition_id', selectedComp);
                        if (selectedClub && selectedClub !== 'all') {
                          params.append('club_id', selectedClub);
                        }
                        const response = await axios.post(
                          `${API}/documents/entry-confirmation?${params.toString()}`,
                          {},
                          { withCredentials: true, responseType: 'blob' }
                        );
                        const contentType = response.headers['content-type'];
                        const isZip = contentType === 'application/zip';
                        const disposition = response.headers['content-disposition'] || '';
                        let filename = isZip ? 'Entry_Confirmations.zip' : 'Entry_Confirmations.docx';
                        const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
                        if (filenameMatch) filename = filenameMatch[1];
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('Entry confirmation(s) generated successfully');
                      } catch (error) {
                        console.error(error);
                        toast.error(error.response?.data?.detail || 'Failed to generate entry confirmation');
                      }
                    }}
                    className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                    data-testid="generate-entry-confirmation"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Generate Entry Confirmation
                  </Button>
                  <div>
                    <p className="text-sm font-medium text-stone-800">Generate and download</p>
                    <p className="text-xs text-stone-600">
                      Downloads a Word document per club. Multiple clubs are bundled into a ZIP.
                    </p>
                  </div>
                </div>
              </Card>

              {/* TILE 2: Email Entry Confirmations to Clubs */}
              <Card className="p-5 space-y-4 border-stone-200 bg-stone-50/50" data-testid="entry-conf-email-tile">
                <div>
                  <h4 className="text-base font-semibold text-stone-800">Email Entry Confirmations to Clubs</h4>
                  <p className="text-sm text-stone-600 mt-1">
                    Generate per-club confirmation letters and send them out by email. Each club only sees their own entries.
                  </p>
                  <LastSentLine competitionId={selectedComp} kind="entry-confirmation" />
                </div>

                <div>
                  <Label htmlFor="ec-email-club" className="text-sm font-medium text-stone-700">
                    Clubs (Select individual clubs or All Clubs Option)
                  </Label>
                  <select
                    id="ec-email-club"
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm"
                    value={entryEmailClub}
                    onChange={(e) => setEntryEmailClub(e.target.value)}
                    data-testid="entry-conf-email-club-select"
                  >
                    <option value="all">All Clubs ({clubsWithEntries.length} clubs)</option>
                    {clubsWithEntries.map(club => (
                      <option key={club.club_id} value={club.club_id}>
                        {club.club_name} ({club.entry_count} entries)
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  onClick={() => setEntryEmailDialogOpen(true)}
                  className="btn-primary"
                  data-testid="email-entry-confirmations-btn"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Email Entry Confirmations
                </Button>
              </Card>
            </>
          )}
        </div>
      );
    }

    // Entry Pack — bulk email to clubs offering matching disciplines
    if (selectedDocument === 'entry_pack') {
      const fetchEligible = async (compId) => {
        if (!compId) { setEntryPackClubs([]); setEntryPackSelectedIds([]); return; }
        setLoadingEntryPackClubs(true);
        try {
          const res = await axios.get(
            `${API}/entry-pack-email/${compId}/eligible-clubs`,
            { withCredentials: true }
          );
          setEntryPackClubs(res.data.clubs || []);
          setEntryPackSelectedIds([]);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to load clubs');
          setEntryPackClubs([]);
        } finally {
          setLoadingEntryPackClubs(false);
        }
      };

      const handleCompChange = (compId) => {
        setEntryPackComp(compId);
        fetchEligible(compId);
      };

      const toggleClub = (clubId) => {
        setEntryPackSelectedIds(prev =>
          prev.includes(clubId)
            ? prev.filter(id => id !== clubId)
            : [...prev, clubId]
        );
      };
      const allSelected = entryPackClubs.length > 0
        && entryPackSelectedIds.length === entryPackClubs.length;
      const toggleAll = () => {
        setEntryPackSelectedIds(allSelected ? [] : entryPackClubs.map(c => c.club_id));
      };

      const competition = competitions.find(c => c.competition_id === entryPackComp) || null;
      const selectedClubs = entryPackClubs.filter(c => entryPackSelectedIds.includes(c.club_id));

      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Entry Pack</h3>
            <p className="text-sm text-muted-foreground">
              Configure and distribute the entry pack — Event Notice, Entry Summary Worksheet, and (where applicable) entry forms.
            </p>
          </div>

          <div className="hidden">
            <Label>Competition</Label>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={entryPackComp}
              onChange={(e) => handleCompChange(e.target.value)}
              data-testid="entry-pack-comp-select"
            >
              <option value="">Select Competition</option>
              {competitions.map(c => (
                <option key={c.competition_id} value={c.competition_id}>{c.name}</option>
              ))}
            </select>
          </div>

          {entryPackComp && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Clubs to receive the Entry Pack</Label>
                {entryPackClubs.length > 0 && (
                  <button
                    type="button"
                    className="text-xs text-stone-600 underline hover:text-stone-800"
                    onClick={toggleAll}
                    data-testid="entry-pack-toggle-all"
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              {loadingEntryPackClubs ? (
                <p className="text-sm text-stone-600">Loading clubs…</p>
              ) : entryPackClubs.length === 0 ? (
                <p className="text-sm text-amber-700">
                  No clubs match the disciplines included in this competition.
                </p>
              ) : (
                <ul
                  className="border rounded-lg divide-y max-h-72 overflow-y-auto"
                  data-testid="entry-pack-clubs-list"
                >
                  {entryPackClubs.map(c => (
                    <li
                      key={c.club_id}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-stone-50 ${entryPackSelectedIds.includes(c.club_id) ? 'bg-stone-100' : ''}`}
                      onClick={() => toggleClub(c.club_id)}
                    >
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          readOnly
                          checked={entryPackSelectedIds.includes(c.club_id)}
                        />
                        <span className="font-medium">{c.name}</span>
                        {c.missing_email && (
                          <span className="text-xs text-amber-700 ml-auto">(no email on file)</span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {entryPackComp && entryPackSelectedIds.length > 0 && (
            <Button
              onClick={() => setEntryPackDialogOpen(true)}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-full"
              data-testid="entry-pack-email-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Email Entry Packs ({entryPackSelectedIds.length})
            </Button>
          )}

          <EntryPackEmailDialog
            open={entryPackDialogOpen}
            onOpenChange={setEntryPackDialogOpen}
            competitionId={entryPackComp}
            competition={competition}
            selectedClubs={selectedClubs}
          />
        </div>
      );
    }

    // Entry Summary Template form
    if (selectedDocument === 'entry_summary') {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Download Entry Summary Template</h3>
            <p className="text-sm text-muted-foreground">
              Download an Excel template pre-configured for collecting entries. The template includes separate worksheets for Individual, Pairs, and Team entries with all applicable grades.
            </p>
          </div>

          <div className="hidden">
            <Label>Competition</Label>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={templateCompetitionId}
              onChange={(e) => setTemplateCompetitionId(e.target.value)}
              data-testid="template-competition-select"
            >
              <option value="">Select Competition</option>
              {competitions.map(c => (
                <option key={c.competition_id} value={c.competition_id}>{c.name}</option>
              ))}
            </select>
          </div>

          {templateCompetitionId && (
            <Button 
              onClick={async () => {
                setDownloadingTemplate(true);
                try {
                  const response = await axios.get(
                    `${API}/competitions/${templateCompetitionId}/entry-template`,
                    { 
                      withCredentials: true,
                      responseType: 'blob'
                    }
                  );
                  
                  // Get filename from content-disposition header or generate one
                  const contentDisposition = response.headers['content-disposition'];
                  let filename = 'Entry_Template.xlsx';
                  if (contentDisposition) {
                    const match = contentDisposition.match(/filename="(.+)"/);
                    if (match) filename = match[1];
                  }
                  
                  // Create download link
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', filename);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  
                  toast.success('Entry template downloaded');
                } catch (error) {
                  console.error('Error downloading template:', error);
                  toast.error('Failed to download template');
                } finally {
                  setDownloadingTemplate(false);
                }
              }}
              className="btn-primary w-full"
              disabled={downloadingTemplate}
              data-testid="download-entry-template"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {downloadingTemplate ? 'Downloading...' : 'Download Entry Template'}
            </Button>
          )}
        </div>
      );
    }

    // Schedule Report form
    if (selectedDocument === 'schedule_report') {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Generate Schedule Report</h3>
            <p className="text-sm text-muted-foreground">
              Generate a comprehensive report of all grades within a schedule, organized by discipline.
            </p>
          </div>

          <div>
            <Label>Select Schedule</Label>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={selectedSchedule}
              onChange={(e) => setSelectedSchedule(e.target.value)}
              data-testid="report-schedule-select"
            >
              <option value="">Select a schedule...</option>
              {schedules.map(schedule => (
                <option key={schedule.schedule_id} value={schedule.schedule_id}>
                  {schedule.schedule_type}
                </option>
              ))}
            </select>
          </div>

          {selectedSchedule && (
            <Button
              onClick={async () => {
                setGeneratingReport(true);
                try {
                  const response = await axios.post(
                    `${API}/documents/grade-report?schedule_id=${selectedSchedule}`,
                    {},
                    { withCredentials: true, responseType: 'blob' }
                  );
                  
                  const contentDisposition = response.headers['content-disposition'];
                  let filename = 'Schedule Report.docx';
                  if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (match) filename = match[1];
                  }
                  
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', filename);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  
                  toast.success('Schedule report generated successfully');
                } catch (error) {
                  console.error(error);
                  toast.error('Failed to generate schedule report');
                } finally {
                  setGeneratingReport(false);
                }
              }}
              className="btn-primary w-full"
              disabled={generatingReport}
              data-testid="generate-schedule-report-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {generatingReport ? 'Generating...' : 'Generate Schedule Report'}
            </Button>
          )}
        </div>
      );
    }

    if (selectedDocument === 'draft_programme') {
      return <DraftProgrammePanel competitionsWithOOS={competitionsWithOOS} competitionId={selectedComp} />;
    }

    if (selectedDocument === 'competition_draws') {
      // Owner spec (Feb 2026): the "Draw for skating order" workflow
      // moved out of the Draft Programme tile into its own step,
      // because the Draft Programme can now be generated without a draw.
      // The Figure Draw panel also lives inside CompetitionDrawsPanel
      // (rendered above the skating-order options).
      return <CompetitionDrawsPanel competitionsWithOOS={competitionsWithOOS} competitionId={selectedComp} />;
    }

    if (selectedDocument === 'final_programme') {
      return <FinalProgrammePanel competitionsWithOOS={competitionsWithOOS} competitionId={selectedComp} />;
    }

    if (selectedDocument === 'run_sheet') {
      return <RunSheetPanel competitionsWithOOS={competitionsWithOOS} competitionId={selectedComp} />;
    }

    if (selectedDocument === 'judges_sheets_figures') {
      return <JudgesSheetsPanel competitionsWithOOS={competitionsWithOOS} competitionId={selectedComp} />;
    }


    return null;
  };

  // Render Document Settings tab content
  const renderSettingsContent = () => (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          onClick={handleSaveSettings} 
          className="btn-primary"
          disabled={!settingsChanged}
          data-testid="save-settings-btn"
        >
          <Save className="mr-2 h-4 w-4" />
          Save Settings
        </Button>
        <Button 
          onClick={handlePreviewSettings}
          variant="outline"
          disabled={previewLoading}
          data-testid="preview-settings-btn"
        >
          <Eye className="mr-2 h-4 w-4" />
          {previewLoading ? 'Generating...' : 'Preview'}
        </Button>
        <Button 
          onClick={handleResetSettings}
          variant="outline"
          data-testid="reset-settings-btn"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
      </div>
      
      <div className="space-y-6" data-testid="document-settings-layout">
      {/* ================================================================
          ROW 1 (top): Document Styling — full-width card combining the
          old Typography + Colours tiles. Contains:
            • Styles Defaults: Default Heading Font + Default Body Font
              (with PDF Fonts panel + help) | Colour Scheme + Line
              Spacing
            • Styles Detail: 8-row table — Style (+ Preview pill) /
              Colour Swatch / Size (pt) / MS365 Font / PDF Font
          See ``utils/document_settings_resolver.py`` on the backend
          for how each style row resolves its effective font.
          ================================================================ */}
      <Card className="p-6" data-testid="document-styling-card">
        <h3 className="text-lg font-semibold mb-4">Document Styling</h3>

        {/* ── Styles Defaults ─────────────────────────────────────── */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          data-testid="styles-defaults"
        >
          {/* Left half — MS365 + PDF font defaults */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              {/* MS365 Fonts column */}
              <div className="space-y-3">
                <h4
                  className="text-xs font-semibold uppercase tracking-wide text-stone-500"
                  data-testid="ms365-fonts-heading"
                >
                  MS365 Font
                </h4>
                <div>
                  <Label>Default Heading Font</Label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    value={settings.headingFont}
                    onChange={(e) => { setSettings({ ...settings, headingFont: e.target.value }); setSettingsChanged(true); }}
                    data-testid="default-heading-font-select"
                    tabIndex={1}
                  >
                    {MS365_FONTS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Default Body Font</Label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    value={settings.bodyFont}
                    onChange={(e) => { setSettings({ ...settings, bodyFont: e.target.value }); setSettingsChanged(true); }}
                    data-testid="default-body-font-select"
                    tabIndex={2}
                  >
                    {MS365_FONTS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* PDF Fonts column */}
              <div className="space-y-3">
                <div className="flex items-center gap-1.5">
                  <h4
                    className="text-xs font-semibold uppercase tracking-wide text-stone-500"
                    data-testid="pdf-fonts-heading"
                  >
                    PDF Fonts
                  </h4>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        aria-label="What are PDF Fonts?"
                        className="text-stone-400 hover:text-stone-600 transition-colors"
                        data-testid="pdf-fonts-help-trigger"
                      >
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="pdf-fonts-help-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>What are PDF Fonts?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Metric- or visually-compatible open-source
                          substitutes used when the document is exported
                          to PDF — these are picked automatically based
                          on your MS365 selection so the layout stays
                          true to the Word original.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogAction data-testid="pdf-fonts-help-okay">
                          Okay
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div>
                  <Label className="text-stone-500">Heading (auto-mapped)</Label>
                  <div
                    className="w-full mt-1 px-3 py-2 border border-dashed border-stone-300 rounded-lg bg-stone-50 text-sm text-stone-700 flex items-center justify-between"
                    data-testid="default-heading-pdf-font-display"
                  >
                    {(() => {
                      const f = pdfFontFor(settings.headingFont);
                      return (
                        <>
                          <span style={{ fontFamily: `"${f.name}", ${f.family}` }}>{f.name}</span>
                          <span className="text-xs text-stone-400 font-mono">↔ {settings.headingFont}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <Label className="text-stone-500">Body (auto-mapped)</Label>
                  <div
                    className="w-full mt-1 px-3 py-2 border border-dashed border-stone-300 rounded-lg bg-stone-50 text-sm text-stone-700 flex items-center justify-between"
                    data-testid="default-body-pdf-font-display"
                  >
                    {(() => {
                      const f = pdfFontFor(settings.bodyFont);
                      return (
                        <>
                          <span style={{ fontFamily: `"${f.name}", ${f.family}` }}>{f.name}</span>
                          <span className="text-xs text-stone-400 font-mono">↔ {settings.bodyFont}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right half — Colour Scheme (top) + Line Spacing (bottom) */}
          <div className="space-y-4">
            <div>
              <Label>Colour Scheme</Label>
              <p
                className="text-xs italic text-stone-500 mt-1.5 mb-1.5"
                data-testid="colour-scheme-edit-hint"
              >
                To edit individual colours choose the Custom scheme
                option. This will use the last saved scheme as a
                starting point.
              </p>
              <select
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={settings.colourScheme}
                onChange={(e) => handleColourSchemeChange(e.target.value)}
                data-testid="colour-scheme-select"
                tabIndex={3}
              >
                <option value="brand">Brand (Greens &amp; Browns)</option>
                <option value="grayscale">Grayscale</option>
                <option value="high_contrast">High Contrast</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <Label>Line Spacing</Label>
              <select
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={settings.lineSpacing}
                onChange={(e) => { setSettings({ ...settings, lineSpacing: parseFloat(e.target.value) }); setSettingsChanged(true); }}
                data-testid="line-spacing-select"
                tabIndex={4}
              >
                <option value="1">Single</option>
                <option value="1.08">1.08 (Default)</option>
                <option value="1.15">1.15</option>
                <option value="1.5">1.5</option>
                <option value="2">Double</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Styles Detail (per-style overrides table) ──────────── */}
        <div className="mt-6" data-testid="styles-detail">
          <h4
            className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3"
            data-testid="styles-detail-heading"
          >
            Styles Detail
          </h4>
          <div className="overflow-x-auto border border-stone-200 rounded-lg">
            <table className="w-full text-sm" data-testid="styles-detail-table">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <th className="px-3 py-2.5 w-[14%]">Style</th>
                  <th className="px-3 py-2.5 w-[8%] text-center">Preview</th>
                  <th className="px-3 py-2.5 w-[25%]">Colour</th>
                  <th className="px-3 py-2.5 w-[10%]">Size (pt)</th>
                  <th className="px-3 py-2.5 w-[22%]">MS365 Font</th>
                  <th className="px-3 py-2.5 w-[21%]">PDF Font</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {(() => {
                  // Preview sample text — selected competition's name
                  // (from the Document Generation tab's dropdown) or
                  // the current-year NZ Artistic Rollersports fallback.
                  const _selectedCompName = competitions.find(
                    (c) => c.competition_id === selectedComp,
                  )?.name;
                  const previewSampleText = _selectedCompName
                    || `${new Date().getFullYear()} New Zealand Artistic Rollersports Championships`;
                  return STYLE_ROWS.map(({ key, label, sizeKey }) => {
                  const isCustomScheme = settings.colourScheme === 'custom';
                  const colour = settings.colours?.[key] || '#000000';
                  const size = settings.fontSizes?.[sizeKey] ?? styleSizeDefaults[key];
                  const explicitFont = settings.styleFonts?.[key] || '';
                  const effectiveFont = resolveStyleFont(settings, key);
                  const pdfFont = pdfFontFor(effectiveFont);
                  const isOverridden = !!explicitFont;
                  return (
                    <tr
                      key={key}
                      className="hover:bg-stone-50/60"
                      data-testid={`style-row-${key}`}
                    >
                      {/* Style name */}
                      <td className="px-3 py-2 align-middle font-medium text-stone-800">
                        {label}
                      </td>

                      {/* Preview pill — opens a popover showing the
                          competition name (or current-year NZ Artistic
                          Champs) rendered in the row's font / size /
                          colour / weight. Lives in its own column so
                          the pills line up vertically. */}
                      <td className="px-3 py-2 align-middle text-center">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-semibold bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-800 transition-colors"
                              title={`Preview ${label} styling`}
                              data-testid={`style-preview-trigger-${key}`}
                            >
                              <Eye className="h-3 w-3" />
                              Preview
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            side="right"
                            sideOffset={8}
                            className="w-[480px] p-0 overflow-hidden"
                            data-testid={`style-preview-popover-${key}`}
                          >
                            <div className="px-4 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                                {label} preview
                              </div>
                              <div className="text-[10px] text-stone-400 font-mono">
                                {effectiveFont} · {size}pt
                              </div>
                            </div>
                            <div className="p-5 bg-white">
                              <p
                                className="m-0"
                                style={{
                                  fontFamily: `"${effectiveFont}", ${pdfFont.family}`,
                                  fontSize: `${size}pt`,
                                  color: colour,
                                  lineHeight: settings.lineSpacing || 1.15,
                                  fontWeight:
                                    key === 'highlight' || key === 'tableHeader'
                                      ? 600
                                      : key.startsWith('h')
                                      ? 700
                                      : 400,
                                  fontStyle: key === 'secondaryText' ? 'italic' : 'normal',
                                }}
                                data-testid={`style-preview-sample-${key}`}
                              >
                                {previewSampleText}
                              </p>
                              <div className="mt-4 pt-3 border-t border-stone-100 text-[10px] text-stone-400 flex items-center gap-3">
                                <span>RGB {hexToRgb(colour)}</span>
                                <span className="font-mono">{colour.toUpperCase()}</span>
                                <span className="ml-auto italic">
                                  PDF font: {pdfFont.name}
                                </span>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </td>

                      {/* Colour swatch + RGB/Hex stack */}
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-2.5">
                          {isCustomScheme ? (
                            <input
                              type="color"
                              value={colour}
                              onChange={(e) => {
                                setSettings({
                                  ...settings,
                                  colours: { ...settings.colours, [key]: e.target.value },
                                });
                                setSettingsChanged(true);
                              }}
                              className="w-9 h-9 rounded cursor-pointer border border-stone-200 flex-shrink-0"
                              data-testid={`style-colour-input-${key}`}
                            />
                          ) : (
                            <span
                              className="w-9 h-9 rounded border border-stone-200 inline-block flex-shrink-0"
                              style={{ backgroundColor: colour }}
                              title={`${colour} — switch Colour Scheme to Custom to edit`}
                              data-testid={`style-colour-swatch-${key}`}
                            />
                          )}
                          <div className="flex flex-col min-w-0 leading-tight">
                            <span
                              className="text-xs font-semibold"
                              style={{ color: colour }}
                              data-testid={`style-rgb-${key}`}
                            >
                              RGB {hexToRgb(colour)}
                            </span>
                            <span
                              className="text-xs font-mono"
                              style={{ color: colour }}
                              data-testid={`style-hex-${key}`}
                            >
                              {colour.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Size (pt) */}
                      <td className="px-3 py-2 align-middle">
                        <Input
                          type="number"
                          min={6}
                          max={72}
                          step={1}
                          value={size}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (Number.isNaN(v)) return;
                            setSettings({
                              ...settings,
                              fontSizes: { ...settings.fontSizes, [sizeKey]: v },
                            });
                            setSettingsChanged(true);
                          }}
                          className="h-9 w-20 text-sm"
                          data-testid={`style-size-input-${key}`}
                        />
                      </td>

                      {/* MS365 Font — "Default" (linked) until user picks a font */}
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-1.5">
                          <select
                            className="h-9 border rounded-lg px-2 text-sm flex-1 min-w-0"
                            value={explicitFont}
                            onChange={(e) => {
                              const v = e.target.value || null;
                              setSettings({
                                ...settings,
                                styleFonts: { ...(settings.styleFonts || {}), [key]: v },
                              });
                              setSettingsChanged(true);
                            }}
                            data-testid={`style-font-select-${key}`}
                          >
                            <option value="">Default ({HEADING_FAMILY_STYLE_KEYS.has(key) ? settings.headingFont : settings.bodyFont})</option>
                            {MS365_FONTS.map((f) => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                          {isOverridden && (
                            <button
                              type="button"
                              onClick={() => {
                                setSettings({
                                  ...settings,
                                  styleFonts: { ...(settings.styleFonts || {}), [key]: null },
                                });
                                setSettingsChanged(true);
                              }}
                              className="text-stone-400 hover:text-stone-600 transition-colors"
                              title="Reset to default"
                              data-testid={`style-font-reset-${key}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* PDF Font — auto-mapped from effective MS365 font */}
                      <td className="px-3 py-2 align-middle">
                        <div
                          className="h-9 px-2 border border-dashed border-stone-300 rounded-lg bg-stone-50 text-xs text-stone-700 flex items-center justify-between gap-1.5"
                          data-testid={`style-pdf-font-${key}`}
                        >
                          <span
                            className="truncate"
                            style={{ fontFamily: `"${pdfFont.name}", ${pdfFont.family}` }}
                          >
                            {pdfFont.name}
                          </span>
                          <span className="text-[10px] text-stone-400 font-mono flex-shrink-0">
                            ↔ {effectiveFont}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* ================================================================
          ROW 2 (bottom): Page Setup | Headers & Footers.
          ================================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Page Setup Card */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Page Setup</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Paper Size</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.paperSize}
                  onChange={(e) => { setSettings({...settings, paperSize: e.target.value}); setSettingsChanged(true); }}
                >
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                </select>
              </div>
              <div>
                <Label>Orientation</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.orientation}
                  onChange={(e) => { setSettings({...settings, orientation: e.target.value}); setSettingsChanged(true); }}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label>Margin Preset</Label>
              <select
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={settings.marginPreset}
                onChange={(e) => {
                  const preset = e.target.value;
                  let margins = settings.margins;
                  if (preset === 'narrow') margins = { top: 1.27, bottom: 1.27, left: 1.27, right: 1.27 };
                  else if (preset === 'standard') margins = { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 };
                  else if (preset === 'wide') margins = { top: 2.5, bottom: 2.5, left: 3.17, right: 3.17 };
                  setSettings({...settings, marginPreset: preset, margins});
                  setSettingsChanged(true);
                }}
              >
                <option value="narrow">Narrow (1.27cm)</option>
                <option value="standard">Standard (2.5cm)</option>
                <option value="wide">Wide (2.5/3.17cm)</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {settings.marginPreset === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Top (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.margins.top}
                    onChange={(e) => { setSettings({...settings, margins: {...settings.margins, top: parseFloat(e.target.value)}}); setSettingsChanged(true); }}
                  />
                </div>
                <div>
                  <Label>Bottom (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.margins.bottom}
                    onChange={(e) => { setSettings({...settings, margins: {...settings.margins, bottom: parseFloat(e.target.value)}}); setSettingsChanged(true); }}
                  />
                </div>
                <div>
                  <Label>Left (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.margins.left}
                    onChange={(e) => { setSettings({...settings, margins: {...settings.margins, left: parseFloat(e.target.value)}}); setSettingsChanged(true); }}
                  />
                </div>
                <div>
                  <Label>Right (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.margins.right}
                    onChange={(e) => { setSettings({...settings, margins: {...settings.margins, right: parseFloat(e.target.value)}}); setSettingsChanged(true); }}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>


        {/* Headers & Footers Card */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Headers & Footers</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeHeader"
                checked={settings.includeHeader}
                onChange={(e) => { setSettings({...settings, includeHeader: e.target.checked}); setSettingsChanged(true); }}
                className="rounded"
              />
              <Label htmlFor="includeHeader">Include Header</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeFooter"
                checked={settings.includeFooter}
                onChange={(e) => { setSettings({...settings, includeFooter: e.target.checked}); setSettingsChanged(true); }}
                className="rounded"
              />
              <Label htmlFor="includeFooter">Include Footer</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeLogo"
                checked={settings.includeLogo}
                onChange={(e) => { setSettings({...settings, includeLogo: e.target.checked}); setSettingsChanged(true); }}
                className="rounded"
              />
              <Label htmlFor="includeLogo">Include Logo in Header</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includePageNumbers"
                checked={settings.includePageNumbers}
                onChange={(e) => { setSettings({...settings, includePageNumbers: e.target.checked}); setSettingsChanged(true); }}
                className="rounded"
              />
              <Label htmlFor="includePageNumbers">Include Page Numbers</Label>
            </div>
            {settings.includePageNumbers && (
              <div>
                <Label>Page Number Format</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.pageNumberFormat}
                  onChange={(e) => { setSettings({...settings, pageNumberFormat: e.target.value}); setSettingsChanged(true); }}
                >
                  <option value="Page X of Y">Page X of Y</option>
                  <option value="X of Y">X of Y</option>
                  <option value="X">X only</option>
                </select>
              </div>
            )}
          </div>
        </Card>
      </div>

      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Settings Preview</h3>
              <Button variant="ghost" size="icon" onClick={closePreview}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 p-4">
              {previewUrl && (
                <iframe src={previewUrl} className="w-full h-full border rounded" title="Document Preview" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render document tiles grid for a tab
  const renderDocumentTilesTab = (documents) => (
    <div className="flex gap-6 h-[calc(100vh-280px)]">
      {/* Left column - Document tiles */}
      <div className="w-80 shrink-0 space-y-3 overflow-y-auto pr-2">
        {documents.map(doc => renderDocumentTile(doc))}
      </div>
      
      {/* Right column - Form panel */}
      <div className="flex-1 bg-white border rounded-lg p-6 overflow-y-auto">
        {renderFormPanel()}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2D4A3E' }}>Document Management</h1>
        <p className="text-muted-foreground mt-1">Generate and manage competition documents</p>
      </div>

      {/* Global competition selector + tab navigation — pinned together so
          they remain accessible while scrolling long document panels. */}
      <div className="sticky top-16 z-20 -mx-6 px-6 pt-3 bg-white/95 backdrop-blur border-b border-stone-200 shadow-sm space-y-3" data-testid="documents-sticky-header">
        <div className="flex items-center gap-3" data-testid="global-comp-selector-row">
          <Label htmlFor="global-comp-selector" className="text-sm font-semibold whitespace-nowrap">
            Select Competition
          </Label>
          <select
            id="global-comp-selector"
            className="flex-1 max-w-[480px] border rounded-lg px-3 py-2 text-sm bg-white"
            value={selectedComp}
            onChange={(e) => setSelectedComp(e.target.value)}
            data-testid="global-comp-selector"
          >
            <option value="">Select a competition…</option>
            {competitions.map((c) => (
              <option key={c.competition_id} value={c.competition_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tab Navigation — Content Library style, with gating tooltips. */}
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const requiresEntries = ['order-programme', 'officials'].includes(tab.id);
            const hasEntries = !!selectedComp && competitionsWithEntries.some(c => c.competition_id === selectedComp);
            const isDisabled = requiresEntries && !hasEntries;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (isDisabled) return;
                  setActiveTab(tab.id);
                  setSelectedDocument(null);
                }}
                disabled={isDisabled}
                title={isDisabled ? 'Import entries first to enable this tab' : undefined}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-[#2D4A3E] text-[#2D4A3E] font-medium'
                    : isDisabled
                      ? 'border-transparent text-stone-300 cursor-not-allowed'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-stone-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {!selectedComp && activeTab !== 'settings' && activeTab !== 'reports' ? (
          <div
            className="rounded-lg border-2 border-dashed border-stone-200 bg-stone-50 p-12 text-center"
            data-testid="no-comp-empty-state"
          >
            <p className="text-base text-stone-700 font-medium">Select a competition to begin</p>
            <p className="text-sm text-stone-500 mt-1">
              Pick a competition from the selector above to see the documents available for it.
            </p>
          </div>
        ) : (
          <>
            {activeTab === 'competition-entries' && renderDocumentTilesTab(competitionEntriesDocs)}
            {activeTab === 'order-programme' && renderDocumentTilesTab(orderProgrammeDocs)}
            {activeTab === 'officials' && renderDocumentTilesTab(officialsDocs)}
            {activeTab === 'wrap-up' && renderDocumentTilesTab(wrapUpDocs)}
            {activeTab === 'reports' && renderDocumentTilesTab(reportsDocs)}
            {activeTab === 'settings' && renderSettingsContent()}
          </>
        )}
      </div>

      {/* Event Notice Dialog */}
      <EventNoticeDialog
        open={eventNoticeDialogOpen}
        onOpenChange={setEventNoticeDialogOpen}
        competition={selectedCompetition}
        onSuccess={() => {
          fetchPublishedDocs(selectedComp);
        }}
      />

      {/* Custom Event Notice Upload Dialog (Feb 2026) */}
      <Dialog open={customUploadOpen} onOpenChange={(v) => { if (!customUploading) setCustomUploadOpen(v); }}>
        <DialogContent className="max-w-lg" data-testid="custom-event-notice-dialog">
          <DialogHeader>
            <DialogTitle>Upload Custom Event Notice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a PDF Event Notice (required) and, optionally, a separate Figure Draw PDF.
              Both files publish to the Competition Page immediately and will be included in the Entry Pack.
            </p>
            <div>
              <Label className="text-sm">Event Notice (PDF) <span className="text-red-600">*</span></Label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="mt-1 w-full text-sm"
                onChange={(e) => setCustomUploadEventFile(e.target.files?.[0] || null)}
                data-testid="custom-event-notice-file"
              />
              {customUploadEventFile && (
                <p className="text-xs text-muted-foreground mt-1">
                  {customUploadEventFile.name} · {(customUploadEventFile.size / 1024).toFixed(0)} KB
                </p>
              )}
            </div>
            <div>
              <Label className="text-sm">Figure Draw (PDF, optional)</Label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="mt-1 w-full text-sm"
                onChange={(e) => setCustomUploadFigureFile(e.target.files?.[0] || null)}
                data-testid="custom-figure-draw-file"
              />
              {customUploadFigureFile && (
                <p className="text-xs text-muted-foreground mt-1">
                  {customUploadFigureFile.name} · {(customUploadFigureFile.size / 1024).toFixed(0)} KB
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Max 10 MB per file. Only PDF files are accepted.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCustomUploadOpen(false)} disabled={customUploading}>Cancel</Button>
              <Button
                className="btn-primary"
                onClick={submitCustomEventNoticeUpload}
                disabled={customUploading || !customUploadEventFile}
                data-testid="submit-custom-event-notice-upload"
              >
                {customUploading ? 'Uploading…' : 'Upload and Publish Event Information to Competition Page'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Competition delete dialog — fired by the Summary Pack flow */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        loading={deleting}
        itemName={
          competitions.find((c) => c.competition_id === deleteTargetId)?.name
          || 'this competition'
        }
        onConfirm={async () => {
          if (!deleteTargetId) return;
          setDeleting(true);
          try {
            await axios.delete(`${API}/competitions/${deleteTargetId}`,
                               { withCredentials: true });
            toast.success('Competition deleted');
            setDeleteDialogOpen(false);
            setSelectedComp('');
            setSelectedDocument(null);
            // Refresh the comps list.
            try {
              const res = await axios.get(`${API}/competitions`,
                                          { withCredentials: true });
              setCompetitions(res.data || []);
            } catch {
              /* non-fatal */
            }
          } catch (err) {
            toast.error(err.response?.data?.detail
                        || 'Failed to delete competition');
          } finally {
            setDeleting(false);
            setDeleteTargetId(null);
          }
        }}
      />

      {/* Entry Confirmation Email Dialog */}
      <CoveringEmailDialog
        open={entryEmailDialogOpen}
        onOpenChange={setEntryEmailDialogOpen}
        competitionId={selectedComp}
        clubId={entryEmailClub}
        endpointBase="entry-confirmation-email"
        title="Email Entry Confirmations to Clubs"
        description="Compose the covering email. Each club receives their own Entry Confirmation .docx as an attachment."
        testIdPrefix="entry-conf-email"
      />

      {/* Reset Order of Skate Confirmation Dialog */}
      <AlertDialog open={showResetOOSDialog} onOpenChange={setShowResetOOSDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Are you sure you want to reset the Order of Skate?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>Resetting the Order of Skate will take you back to the start of the process. All user changes completed so far will be deleted. This action cannot be undone.</p>
              <p className="font-semibold">Would you like to proceed with the reset?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-bold text-red-600 hover:text-red-700">
              NO
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={async () => {
                try {
                  await axios.delete(
                    `${API}/order-of-skate/${oosSelectedComp}/all`,
                    { withCredentials: true }
                  );
                  toast.success('Order of Skate has been reset');
                  setOosPlanningSettings(null);
                  setOosScheduleExists(false);
                  fetchOOSCompetitionData(oosSelectedComp);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to reset');
                }
              }}
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
